import { Spinner } from '@Components/common/Spinner';
import { LOGIN_REDIRECT } from '@Constants/index';
import { useAuth } from '@Contexts/AuthContext/Auth.context';
import usePrice from '@Hooks/usePrice';
import { calculateFiveStarRating } from '@Utils/Rating';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Add from 'public/add_to_cart.svg';
import Fixer from 'public/fixer.svg';
import HeartFilled from 'public/heart-filled.svg';
import Logo from 'public/logo.svg';
import Mobil from 'public/mobil-logo.svg';
import RedHeart from 'public/red_heart.svg';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCookie } from 'react-use';
import { Dispatch, RootState } from 'store';
import { APIResponse } from 'types/APIResponse';
import { PartCategory } from 'types/Categories';
import { Product } from 'types/Products';
import { Rate } from 'types/Rate';
import { Review } from 'types/Review';
import {
  Actions,
  AddToCard,
  BluePrice,
  CartIcon,
  Description,
  Feature,
  FeaturesList,
  FeaturesWrapper,
  Header,
  HeaderCategory,
  HeaderReview,
  Icon,
  IconWrapper,
  Like,
  Number,
  P,
  Price,
  QuantityBtn,
  QuantityWrapper,
  Reviews,
  StyledRate,
  SubCategory,
  Wrapper,
} from './ProductDetails.styled';

type Props = {
  data: Product;
  reviewData?: APIResponse<Review[]>;
  rateData?: Rate;
};
export default function ProductDetails({ data, reviewData, rateData }: Props) {
  const { t } = useTranslation('Product');
  const { locale, asPath, push, pathname } = useRouter();
  const { data: categories } = useSelector((state: RootState) => state.categories);
  const getCategory: (categories: PartCategory[]) => any = (categories) => {
    return categories.find((cat) => cat.id === data.category.id || getCategory(cat.children))?.image;
  };
  const [, updateCookieRedirect] = useCookie(LOGIN_REDIRECT);
  const { userData } = useAuth();
  const { thePrice } = usePrice(data.price);
  const { thePrice: specialPrice } = usePrice(data.special_price || 0);
  const { cart: dispatchCart } = useDispatch<Dispatch>();
  const { data: cartData } = useSelector((state: RootState) => state.cart);
  const { data: wishListData } = useSelector((state: RootState) => state.profileWishlist);
  const [isLoading, setIsLoading] = useState(false);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const { profileWishlist: dispatchWishlist } = useDispatch<Dispatch>();
  const productInCart = useMemo(
    () => cartData?.items.find((pr) => pr.product.id === data.id),
    [cartData?.items, data.id]
  );
  const productInWishlist = useMemo(() => wishListData?.find((pr) => pr.id === data.id), [data.id, wishListData]);

  const handleAddToCart = useCallback(() => {
    setIsLoading(true);
    dispatchCart
      .addProductToCart({
        product_id: data.id,
        quantity: 1,
      })
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [data, dispatchCart]);

  const updateProductInCart = useCallback(
    (increase) => {
      if (productInCart) {
        setIsLoading(true);
        if (increase) {
          dispatchCart
            .updateProductInCart({
              product_id: productInCart.product.id,
              quantity: productInCart.quantity + 1,
            })
            .then(() => setIsLoading(false))
            .catch(() => setIsLoading(false));
        } else {
          if (productInCart.quantity === 1) {
            dispatchCart.deleteProductFromCart(productInCart.product.id).then(() => setIsLoading(false));
          } else {
            dispatchCart
              .updateProductInCart({
                product_id: productInCart.product.id,
                quantity: productInCart.quantity - 1,
              })
              .then(() => setIsLoading(false))
              .catch(() => setIsLoading(false));
          }
        }
      }
    },
    [dispatchCart, productInCart]
  );
  const handleToggleWishlist = useCallback(
    (inWishlist?: boolean) => {
      if (Boolean(userData)) {
        setIsWishlistLoading(true);
        if (inWishlist) {
          dispatchWishlist.deleteProductFromWishlist(data.id).then(() => {
            setIsWishlistLoading(false);
          });
        } else {
          dispatchWishlist.addProductToWishlist(data.id).then(() => {
            setIsWishlistLoading(false);
          });
        }
      } else {
        push('/auth/login').then(() => {
          updateCookieRedirect(
            JSON.stringify({
              asPath,
              pathname,
            })
          );
        });
      }
    },
    [asPath, data.id, dispatchWishlist, pathname, push, updateCookieRedirect, userData]
  );

  return (
    <Wrapper>
      <Header>
        <HeaderCategory>
          <Image src={data.brand.image} width={85.64} height={16} objectFit="contain" alt="brand logo" />
          {categories && (
            <IconWrapper href={`/products?category_id=${data.category.id}`}>
              <Icon>
                {getCategory(categories) && (
                  <Image src={getCategory(categories).image || Logo} width={24} height={24} alt="tyre icon" />
                )}
              </Icon>
              <P>{data.category.name}</P>
            </IconWrapper>
          )}
        </HeaderCategory>
        {rateData && (
          <HeaderReview>
            <Number>{calculateFiveStarRating(rateData)}</Number>
            <StyledRate
              allowHalf={true}
              allowClear={false}
              value={calculateFiveStarRating(rateData)}
              style={{ fontSize: 14 }}
              disabled
              character={<i className="anticon anticon-star" />}
              direction={locale === 'ar' ? 'rtl' : 'ltr'}
            />
            {Boolean(reviewData?.count) && <Reviews>{reviewData?.count}</Reviews>}
          </HeaderReview>
        )}
      </Header>
      <SubCategory>{t('product_name')}</SubCategory>
      <Description>{data.name}</Description>
      {/* <Description>{data.description}</Description> */}
      {data.special_price && data.special_price !== data.price && <Price>{thePrice}</Price>}
      <BluePrice>{data.special_price && data.special_price !== data.price ? specialPrice : thePrice}</BluePrice>
      <FeaturesWrapper>
        <FeaturesList>
          {Boolean(data.can_picked_up) && (
            <Feature>
              {t('available')}
              <Image src={Mobil} width={60} height={20} alt="mobil logo" />
            </Feature>
          )}
          {Boolean(data.installation_price) && (
            <Feature>
              {t('fix')}
              <Image src={Fixer} width={100} height={25} alt="Fixer" />
            </Feature>
          )}
        </FeaturesList>
      </FeaturesWrapper>
      <Actions>
        <Like
          disabled={!Boolean(cartData?.id) || isWishlistLoading}
          onClick={() => handleToggleWishlist(Boolean(productInWishlist))}
        >
          <Image src={productInWishlist ? HeartFilled : RedHeart} width={18} height={16} alt="like icon" />
        </Like>
        {productInCart ? (
          <QuantityWrapper>
            <QuantityBtn
              variant="filled-red"
              disabled={isLoading || !Boolean(cartData?.id)}
              onClick={() => updateProductInCart(false)}
            >
              -
            </QuantityBtn>
            {productInCart.quantity}
            <QuantityBtn
              variant="filled-red"
              disabled={isLoading || !Boolean(cartData?.id)}
              onClick={() => updateProductInCart(true)}
            >
              +
            </QuantityBtn>
          </QuantityWrapper>
        ) : (
          <AddToCard onClick={handleAddToCart}>
            {isLoading ? (
              <Spinner />
            ) : (
              <Fragment>
                {t('add_to_cart')}
                <CartIcon>
                  <Image src={Add} alt="add to cart" />
                </CartIcon>
              </Fragment>
            )}
          </AddToCard>
        )}
      </Actions>
    </Wrapper>
  );
}
